/**
 * Every column and filter a document list can offer (INV-016).
 *
 * The column chooser and the saved-view dialog used to offer only a type's
 * `columns` — six for a project — while the reference's "Column you can add"
 * runs to Credit Period, External ID, Consignee, Tax Unit, Remarks, Billing
 * Method, Created On and Created By. Two sources fill the gap, for every type
 * at once:
 *
 *   1. The audit columns every document has: who created, submitted and
 *      approved it and when, its tax unit, job type, currency, revision and
 *      the approval it is waiting at.
 *   2. The type's own header fields — anything it asks on its form that a
 *      list cell can show (text, numbers, dates, choices, yes/no).
 *
 * The default view is still `def.columns`; this is what may be ADDED. The
 * server fills every key here on each list row (framework/list-catalogue.ts)
 * and accepts every filter here, and the saved-view allow-list is this same
 * catalogue — the three cannot drift apart.
 */
import type { ColumnDef, ControlKind, DocumentDef, FieldDef, FilterDef } from './types.js'
import type { MasterColumnDef, MasterDef } from '../masters/types.js'
import { SYNCABLE_DOC_TYPES } from '../integration/registry.js'

/** The audit and header columns every list can show. */
export const COMMON_LIST_COLUMNS: readonly ColumnDef[] = [
  { key: 'statusStage', label: 'Waiting at', type: 'text', width: 180 },
  { key: 'taxUnitName', label: 'Tax unit', type: 'text', width: 180 },
  { key: 'jobTypeName', label: 'Job type', type: 'text', width: 160 },
  { key: 'currencyCode', label: 'Currency', type: 'text', width: 100 },
  { key: 'remarks', label: 'Remarks', type: 'text', width: 240 },
  { key: 'revisionNo', label: 'Revision', type: 'text', width: 100, align: 'right' },
  { key: 'createdAt', label: 'Created on', type: 'date', width: 128 },
  { key: 'createdByName', label: 'Created by', type: 'user', width: 160 },
  { key: 'submittedAt', label: 'Submitted on', type: 'date', width: 128 },
  { key: 'submittedByName', label: 'Submitted by', type: 'user', width: 160 },
  { key: 'approvedAt', label: 'Approved on', type: 'date', width: 128 },
  { key: 'approvedByName', label: 'Approved by', type: 'user', width: 160 },
]

/**
 * Where a document stands with the accounting system (INV-003), on every
 * list of a type that syncs: the sync state and the voucher it became there.
 */
export const INTEGRATION_LIST_COLUMNS: readonly ColumnDef[] = [
  { key: 'integrationStatus', label: 'Integration status', type: 'text', width: 170 },
  { key: 'integrationRef', label: 'Tally ref', type: 'text', width: 160 },
]

/** The filters every list understands, beyond what a type declares. */
export const COMMON_LIST_FILTERS: readonly FilterDef[] = [
  { key: 'taxUnitId', label: 'Tax unit', control: 'lookup', lookup: { entity: 'tax_unit' } },
  { key: 'jobTypeId', label: 'Job type', control: 'lookup', lookup: { entity: 'job_type' } },
  { key: 'currencyCode', label: 'Currency', control: 'text' },
  { key: 'stage', label: 'Waiting at (approval stage)', control: 'text' },
  { key: 'createdById', label: 'Created by', control: 'lookup', lookup: { entity: 'employee' } },
  { key: 'approvedById', label: 'Approved by', control: 'lookup', lookup: { entity: 'employee' } },
]

/** Header fields whose value a list cell can show as it is. */
const PLAIN: ReadonlySet<ControlKind> = new Set(['text', 'number', 'date', 'money', 'qty', 'rate', 'percent'])
/** …and those it shows by their option's label, under `<key>Text`. */
const WORDED: ReadonlySet<ControlKind> = new Set(['select', 'radio', 'checkbox'])

/** The key a header field is listed under. */
export const listKeyOf = (f: Pick<FieldDef, 'key' | 'control'>): string =>
  WORDED.has(f.control) ? `${f.key}Text` : f.key

const headerFields = (def: DocumentDef): FieldDef[] => {
  const out = new Map<string, FieldDef>()
  // The form's wording first: it is what the document reads as afterwards.
  for (const f of [...def.form.sections.flatMap((s) => s.fields), ...def.createWizard.fields]) {
    if (!out.has(f.key)) out.set(f.key, f)
  }
  return [...out.values()].filter((f) => PLAIN.has(f.control) || WORDED.has(f.control))
}

const COLUMN_TYPE: Partial<Record<ControlKind, ColumnDef['type']>> = {
  date: 'date', money: 'money', qty: 'qty', rate: 'rate', percent: 'rate',
}

/** The header fields a list row carries as words: choices and yes/no. */
export const wordedFields = (def: DocumentDef): FieldDef[] =>
  headerFields(def).filter((f) => WORDED.has(f.control))

export interface ListCatalogue {
  readonly columns: readonly ColumnDef[]
  readonly filters: readonly FilterDef[]
}

const cache = new Map<string, ListCatalogue>()

/** The type's own columns first, then the common ones, then its header fields. */
export const documentListCatalogue = (def: DocumentDef): ListCatalogue => {
  const hit = cache.get(def.key)
  if (hit) return hit
  const columns: ColumnDef[] = [...def.columns]
  const seen = new Set(columns.map((c) => c.key))
  const add = (c: ColumnDef): void => {
    if (seen.has(c.key)) return
    seen.add(c.key)
    columns.push(c)
  }
  COMMON_LIST_COLUMNS.forEach(add)
  if ((SYNCABLE_DOC_TYPES as readonly string[]).includes(def.key)) INTEGRATION_LIST_COLUMNS.forEach(add)
  for (const f of headerFields(def)) {
    // Already a column of the type's own, read by its label (list-labels.ts).
    if (seen.has(f.key)) continue
    const type = COLUMN_TYPE[f.control]
    add({
      key: listKeyOf(f),
      label: f.label,
      type: type ?? 'text',
      ...(type && type !== 'date' ? { align: 'right' as const } : {}),
    })
  }

  const filters: FilterDef[] = [...def.filters]
  const filterKeys = new Set(filters.map((x) => x.key))
  for (const x of COMMON_LIST_FILTERS) if (!filterKeys.has(x.key)) filters.push(x)

  const out = { columns, filters }
  cache.set(def.key, out)
  return out
}

/* ---- masters (SET-030) ---------------------------------------------------- */

/**
 * What a master or setup list may add: when a record was created and by
 * whom — which the reference offers on every setup list — on top of the
 * master's own columns.
 */
export const COMMON_MASTER_COLUMNS: readonly MasterColumnDef[] = [
  { key: 'created_at', label: 'Created on', type: 'date', width: 128 },
  { key: 'created_by_name', label: 'Created by', width: 160 },
]

/**
 * The filters a master list offers: a Name box, every choice, yes/no and
 * reference field on its form, filtered by equality, and Active (Yes/No)
 * where records are retired rather than deleted — the reference's Filters
 * panel on every setup list (`Name`, `Access To`, `Active`).
 */
export const masterListFilters = (
  def: Pick<MasterDef, 'fields' | 'fixed'> & Partial<Pick<MasterDef, 'softDelete'>>,
): FilterDef[] => [
  ...(def.fields.some((f) => f.key === 'name' && f.control === 'text') && !(def.fixed && 'name' in def.fixed)
    ? [{ key: 'name', label: 'Name', control: 'text' as const }] : []),
  ...def.fields
    .filter((f) => !(def.fixed && f.key in def.fixed))
    .flatMap((f): FilterDef[] => {
      if (f.control === 'select' || f.control === 'radio') {
        return [{ key: f.key, label: f.label, control: 'select', options: f.options ?? [] }]
      }
      if (f.control === 'checkbox') {
        return [{ key: f.key, label: f.label, control: 'select', options: [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] }]
      }
      if (f.control === 'lookup' && f.lookup) {
        return [{ key: f.key, label: f.label, control: 'lookup', lookup: { entity: f.lookup.entity } }]
      }
      return []
    }),
  ...(def.softDelete
    ? [{ key: 'active', label: 'Active', control: 'select' as const, options: [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] }]
    : []),
]

/** The Active column every retirable master list shows (the reference's `Name | Active`). */
const ACTIVE_COLUMN: MasterColumnDef = { key: 'active', label: 'Active', type: 'boolean', width: 100 }

export const masterListColumns = (def: Pick<MasterDef, 'columns'> & Partial<Pick<MasterDef, 'softDelete'>>): MasterColumnDef[] => [
  ...def.columns,
  ...(def.softDelete && !def.columns.some((d) => d.key === 'active') ? [ACTIVE_COLUMN] : []),
  ...COMMON_MASTER_COLUMNS.filter((c) => !def.columns.some((d) => d.key === c.key)),
]
